import { create } from 'zustand';

/** Sorted + deduped copy — simulation id sets must be stable for query keys (I3). */
export const normalizeSupplierIds = (ids) => [...new Set(ids || [])].sort();

/**
 * Verification-risk simulation state for the CBAM Costs page.
 *
 * Two id sets, deliberately separate:
 *   draftSupplierIds   — what the drawer is currently editing
 *   appliedSupplierIds — what the page-wide simulation is running on
 * Apply copies draft → applied; the page only ever reads the applied set.
 *
 * A loaded scenario keeps its saved id set in scenarioSupplierIds so the
 * banner can flag the simulation as "(modified)" once the applied set
 * diverges from what was persisted.
 */
export const useSimulationStore = create((set) => ({
  isDrawerOpen: false,
  draftSupplierIds: [],
  appliedSupplierIds: [],
  isSimulating: false,

  // Linkage to a saved scenario (phase 3). Null when the simulation is ad hoc.
  scenarioId: null,
  scenarioName: null,
  scenarioSupplierIds: null,

  setDrawerOpen: (isDrawerOpen) => set({ isDrawerOpen }),

  toggleDraftSupplier: (id) =>
    set((s) => ({
      draftSupplierIds: s.draftSupplierIds.includes(id)
        ? s.draftSupplierIds.filter((x) => x !== id)
        : [...s.draftSupplierIds, id],
    })),

  setDraftSupplierIds: (ids) => set({ draftSupplierIds: [...(ids || [])] }),

  resetDraft: () => set({ draftSupplierIds: [] }),

  applyDraft: () =>
    set((s) => ({
      appliedSupplierIds: normalizeSupplierIds(s.draftSupplierIds),
      isSimulating: s.draftSupplierIds.length > 0,
    })),

  /** Load a persisted scenario into both draft and applied state. */
  loadScenario: (doc) => {
    const ids = normalizeSupplierIds(doc?.assumeDefaultSupplierIds);
    set({
      draftSupplierIds: ids,
      appliedSupplierIds: ids,
      isSimulating: ids.length > 0,
      scenarioId: doc?._id || null,
      scenarioName: doc?.name || null,
      scenarioSupplierIds: ids,
    });
  },

  /** After saving, point the linkage at the (possibly new) scenario doc. */
  linkScenario: (doc) =>
    set({
      scenarioId: doc?._id || null,
      scenarioName: doc?.name || null,
      scenarioSupplierIds: normalizeSupplierIds(doc?.assumeDefaultSupplierIds),
    }),

  clearScenarioLink: () => set({ scenarioId: null, scenarioName: null, scenarioSupplierIds: null }),

  exitSimulation: () =>
    set({
      draftSupplierIds: [],
      appliedSupplierIds: [],
      isSimulating: false,
      scenarioId: null,
      scenarioName: null,
      scenarioSupplierIds: null,
    }),
}));

/** True when a loaded scenario's applied set no longer matches what was saved. */
export const isScenarioModified = (state) => {
  if (!state.scenarioId || !state.scenarioSupplierIds) return false;
  const applied = normalizeSupplierIds(state.appliedSupplierIds);
  const saved = state.scenarioSupplierIds;
  return applied.length !== saved.length || applied.some((id, i) => id !== saved[i]);
};

export default useSimulationStore;
